import type { WavesChart } from '@shared/wavesChart';
import type { WavesEngineState } from '@shared/engine/waves/state';
import {
  DEFAULT_KEY_LABEL,
  SCORE_HUD_X,
  SCORE_HUD_Y,
} from './wavesRenderConstants';

/**
 * Paint the corner HUDs: score, combo, "hold X" hint. Score anchors
 * top-LEFT (not top-right) so the leaderboard panel — which docks
 * top-right at z-index 5 above the canvas — never covers it. Combo
 * stacks below the score in the same column.
 */
export function drawWavesHud(
  ctx: CanvasRenderingContext2D,
  chart: WavesChart,
  state: WavesEngineState,
  displayedScore: number,
  canvasHeight: number,
  keyLabel: string,
): void {
  const palette = chart.palette;
  ctx.save();
  ctx.fillStyle = palette.ui;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  ctx.font = '700 28px "Space Grotesk", system-ui, sans-serif';
  ctx.fillText(formatScore(displayedScore), SCORE_HUD_X, SCORE_HUD_Y);

  ctx.font = '600 18px "Space Grotesk", system-ui, sans-serif';
  ctx.globalAlpha = 0.75;
  ctx.fillText(`Combo x${state.combo}`, SCORE_HUD_X, SCORE_HUD_Y + 36);
  if (state.maxCombo > state.combo) {
    ctx.fillText(`Best x${state.maxCombo}`, SCORE_HUD_X, SCORE_HUD_Y + 60);
  }

  // Hold-button hint anchored bottom-left while playing.
  ctx.globalAlpha = 0.55;
  ctx.font = '500 14px "Space Grotesk", system-ui, sans-serif';
  ctx.textBaseline = 'bottom';
  ctx.fillText(`Hold ${keyLabel || DEFAULT_KEY_LABEL} to climb`, SCORE_HUD_X, canvasHeight - 24);

  ctx.restore();
}

/**
 * Return the score HUD's canvas target — used by score-pop flyers
 * so the "+N" labels travel toward where they'll be added. Lands
 * roughly mid-glyph over the score text (28px font, top-baseline).
 */
export function getWavesScoreHudTarget(): { x: number; y: number } {
  return { x: SCORE_HUD_X + 56, y: SCORE_HUD_Y + 14 };
}

function formatScore(score: number): string {
  const rounded = Math.round(score);
  if (rounded !== cachedScoreRounded) {
    cachedScoreRounded = rounded;
    cachedScoreText = rounded.toLocaleString('en-US');
  }
  return cachedScoreText;
}

let cachedScoreRounded = -1;
let cachedScoreText = '0';
